import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";

const COUNTRY_OPTIONS = [
  { name: "Algeria", code: "+213", min: 9, max: 9 },
  { name: "Argentina", code: "+54", min: 10, max: 10 },
  { name: "Australia", code: "+61", min: 9, max: 9 },
  { name: "Bangladesh", code: "+880", min: 10, max: 10 },
  { name: "Belgium", code: "+32", min: 9, max: 9 },
  { name: "Botswana", code: "+267", min: 8, max: 8 },
  { name: "Brazil", code: "+55", min: 10, max: 11 },
  { name: "Cameroon", code: "+237", min: 9, max: 9 },
  { name: "Canada", code: "+1", min: 10, max: 10 },
  { name: "China", code: "+86", min: 11, max: 11 },
  { name: "Egypt", code: "+20", min: 10, max: 10 },
  { name: "Ethiopia", code: "+251", min: 9, max: 9 },
  { name: "France", code: "+33", min: 9, max: 9 },
  { name: "Germany", code: "+49", min: 10, max: 11 },
  { name: "Ghana", code: "+233", min: 9, max: 9 },
  { name: "India", code: "+91", min: 10, max: 10 },
  { name: "Ireland", code: "+353", min: 9, max: 9 },
  { name: "Italy", code: "+39", min: 9, max: 10 },
  { name: "Japan", code: "+81", min: 10, max: 10 },
  { name: "Kenya", code: "+254", min: 9, max: 9 },
  { name: "Morocco", code: "+212", min: 9, max: 9 },
  { name: "Namibia", code: "+264", min: 9, max: 9 },
  { name: "Netherlands", code: "+31", min: 9, max: 9 },
  { name: "Nigeria", code: "+234", min: 10, max: 10 },
  { name: "Pakistan", code: "+92", min: 10, max: 10 },
  { name: "Qatar", code: "+974", min: 8, max: 8 },
  { name: "Rwanda", code: "+250", min: 9, max: 9 },
  { name: "Saudi Arabia", code: "+966", min: 9, max: 9 },
  { name: "South Africa", code: "+27", min: 9, max: 9 },
  { name: "South Sudan", code: "+211", min: 9, max: 9 },
  { name: "Spain", code: "+34", min: 9, max: 9 },
  { name: "Sweden", code: "+46", min: 9, max: 9 },
  { name: "Switzerland", code: "+41", min: 9, max: 9 },
  { name: "Tanzania", code: "+255", min: 9, max: 9 },
  { name: "Turkey", code: "+90", min: 10, max: 10 },
  { name: "Uganda", code: "+256", min: 9, max: 9 },
  { name: "United Arab Emirates", code: "+971", min: 9, max: 9 },
  { name: "United Kingdom", code: "+44", min: 10, max: 10 },
  { name: "United States", code: "+1", min: 10, max: 10 },
  { name: "Zambia", code: "+260", min: 9, max: 9 },
  { name: "Zimbabwe", code: "+263", min: 9, max: 9 }
].sort((a, b) => a.name.localeCompare(b.name));

const LANGUAGE_OPTIONS = [
  "Arabic",
  "English",
  "French",
  "Kinyarwanda",
  "Luganda",
  "Luo",
  "Portuguese",
  "Runyankole",
  "Somali",
  "Spanish",
  "Swahili",
  "Xitsonga",
  "Yoruba",
  "Zulu"
].sort((a, b) => a.localeCompare(b));

const COUNTRY_BY_NAME = new Map(COUNTRY_OPTIONS.map((item) => [item.name.toLowerCase(), item]));

const emptyProfile = {
  firstName: "",
  lastName: "",
  country: "",
  city: "",
  spokenLanguage: "",
  whatsapp: ""
};

function normalizeCountryName(value) {
  const input = String(value || "").trim();
  if (!input) return "";
  const match = COUNTRY_OPTIONS.find(
    (item) => item.name.toLowerCase() === input.toLowerCase()
  );
  return match ? match.name : input;
}

function splitPhoneNumber(fullNumber, country) {
  const normalizedCountry = normalizeCountryName(country);
  const config = COUNTRY_BY_NAME.get(normalizedCountry.toLowerCase());
  const digits = String(fullNumber || "").replace(/[^\d+]/g, "");
  if (!config) {
    return { countryCode: "", localNumber: digits.replace(/\D/g, "") };
  }

  const localNumber = digits.startsWith(config.code)
    ? digits.slice(config.code.length).replace(/\D/g, "")
    : digits.replace(/\D/g, "");

  return {
    countryCode: config.code,
    localNumber
  };
}

function detectCountryFromLocale() {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale || "";
    const region = locale.split("-")[1];
    if (!region || typeof Intl.DisplayNames !== "function") {
      return "";
    }

    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    const countryName = displayNames.of(region.toUpperCase()) || "";
    return normalizeCountryName(countryName);
  } catch {
    return "";
  }
}

const AdminProfileSetup = () => {
  const navigate = useNavigate();
  const apiBase = useMemo(() => config.API_BASE_URL || "http://localhost:5003", []);
  const token = useMemo(() => localStorage.getItem("token"), []);
  const [form, setForm] = useState(emptyProfile);
  const [countryCode, setCountryCode] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [locationHint, setLocationHint] = useState("We can pre-fill your location to save time.");
  const [geoLoading, setGeoLoading] = useState(false);

  const selectedCountryConfig = useMemo(() => {
    const normalized = normalizeCountryName(form.country);
    return COUNTRY_BY_NAME.get(normalized.toLowerCase()) || null;
  }, [form.country]);

  useEffect(() => {
    if (!token) return;
    fetch(`${apiBase}${config.ADMIN.PROFILE}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (!payload) return;
        const country = normalizeCountryName(payload.country || "");
        const phoneParts = splitPhoneNumber(payload.whatsapp || "", country);
        setForm({
          firstName: payload.firstName || "",
          lastName: payload.lastName || "",
          country,
          city: payload.city || "",
          spokenLanguage: payload.spokenLanguage || "",
          whatsapp: payload.whatsapp || ""
        });
        setCountryCode(phoneParts.countryCode);
        setLocalPhone(phoneParts.localNumber);
      })
      .catch(() => {});
  }, [apiBase, token]);

  useEffect(() => {
    setForm((prev) => {
      if (prev.country) return prev;
      const detectedCountry = detectCountryFromLocale();
      if (!detectedCountry) return prev;
      return { ...prev, country: detectedCountry };
    });
  }, []);

  useEffect(() => {
    if (!selectedCountryConfig) {
      setCountryCode("");
      setForm((prev) => ({
        ...prev,
        whatsapp: localPhone ? localPhone : prev.whatsapp
      }));
      return;
    }

    setCountryCode(selectedCountryConfig.code);
    setForm((prev) => ({
      ...prev,
      country: selectedCountryConfig.name,
      whatsapp: `${selectedCountryConfig.code}${localPhone}`
    }));
  }, [selectedCountryConfig, localPhone]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const requestLocationAutofill = () => {
    if (!navigator.geolocation) {
      setLocationHint("Location autofill is not supported on this device.");
      return;
    }

    setGeoLoading(true);
    setLocationHint("Checking your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          const payload = await response.json().catch(() => ({}));
          const address = payload?.address || {};
          const detectedCountry = normalizeCountryName(address.country || detectCountryFromLocale());
          const detectedCity =
            address.city ||
            address.town ||
            address.village ||
            address.state_district ||
            "";

          setForm((prev) => ({
            ...prev,
            country: prev.country || detectedCountry,
            city: prev.city || detectedCity
          }));

          setLocationHint(
            detectedCountry || detectedCity
              ? "We filled in what we could. Please review before continuing."
              : "We could not determine your exact location, so you can enter it manually."
          );
        } catch {
          setLocationHint("We could not detect your location, so you can enter it manually.");
        } finally {
          setGeoLoading(false);
        }
      },
      () => {
        setGeoLoading(false);
        setLocationHint("Location access was skipped. You can enter your details manually.");
      },
      { enableHighAccuracy: false, timeout: 7000, maximumAge: 300000 }
    );
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.firstName.trim()) nextErrors.firstName = "Please enter your first name";
    if (!form.lastName.trim()) nextErrors.lastName = "Please enter your last name";
    if (!selectedCountryConfig) nextErrors.country = "Please select a country from the list";
    if (!form.city.trim()) nextErrors.city = "Please enter your city";
    if (!form.spokenLanguage.trim()) nextErrors.spokenLanguage = "Please choose your preferred communication language";
    if (!localPhone.trim()) {
      nextErrors.whatsapp = "Please enter a valid phone number";
    } else if (selectedCountryConfig) {
      const digits = localPhone.replace(/\D/g, "");
      if (digits.length < selectedCountryConfig.min || digits.length > selectedCountryConfig.max) {
        nextErrors.whatsapp = "Please enter a valid phone number";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError("");

    try {
      const response = await fetch(`${apiBase}${config.ADMIN.PROFILE_SETUP}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          country: selectedCountryConfig?.name || normalizeCountryName(form.country),
          city: form.city.trim(),
          spokenLanguage: form.spokenLanguage.trim(),
          whatsapp: `${countryCode}${localPhone.replace(/\D/g, "")}`
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to save profile");
      }

      navigate(payload?.redirectTo || "/admin/create-group");
    } catch (err) {
      setError(err.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={page}>
      <form style={card} onSubmit={submit}>
        <div style={hero}>
          <div>
            <h1 style={title}>Set Up Your Kin-Keeper Profile</h1>
            <p style={subtitle}>
              A few quick details to get your Family Circle workspace ready.
            </p>
          </div>
          <button
            type="button"
            style={locationButton}
            onClick={requestLocationAutofill}
            disabled={geoLoading}
          >
            {geoLoading ? "Detecting..." : "Use My Location"}
          </button>
        </div>

        <p style={hintBanner}>{locationHint}</p>

        <section style={sectionCard}>
          <div style={sectionHeader}>
            <h2 style={sectionTitle}>Personal Info</h2>
            <p style={sectionText}>Basic details about you.</p>
          </div>
          <div style={gridTwo}>
            <Field
              label="First Name"
              helper="Your preferred first name"
              error={errors.firstName}
            >
              <input
                style={inputStyle(Boolean(errors.firstName))}
                value={form.firstName}
                placeholder="e.g. Sarah"
                onChange={(e) => update("firstName", e.target.value)}
              />
            </Field>
            <Field
              label="Last Name"
              helper="Your family name"
              error={errors.lastName}
            >
              <input
                style={inputStyle(Boolean(errors.lastName))}
                value={form.lastName}
                placeholder="e.g. Namutebi"
                onChange={(e) => update("lastName", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section style={sectionCard}>
          <div style={sectionHeader}>
            <h2 style={sectionTitle}>Location</h2>
            <p style={sectionText}>Where you are based.</p>
          </div>
          <div style={gridTwo}>
            <Field
              label="Country"
              helper="Select where you are located"
              error={errors.country}
            >
              <input
                list="country-options"
                style={inputStyle(Boolean(errors.country))}
                value={form.country}
                placeholder="Search for your country"
                onChange={(e) => update("country", e.target.value)}
              />
              <datalist id="country-options">
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.name} value={country.name} />
                ))}
              </datalist>
            </Field>
            <Field
              label="City"
              helper="Your current city"
              error={errors.city}
            >
              <input
                style={inputStyle(Boolean(errors.city))}
                value={form.city}
                placeholder="e.g. Kampala"
                onChange={(e) => update("city", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section style={sectionCard}>
          <div style={sectionHeader}>
            <h2 style={sectionTitle}>Contact Info</h2>
            <p style={sectionText}>How we can reach you.</p>
          </div>
          <div style={gridTwo}>
            <Field
              label="Phone"
              helper="Used to contact you and your group"
              error={errors.whatsapp}
            >
              <div style={phoneRow}>
                <input
                  style={codeInput}
                  value={countryCode}
                  aria-label="Country code"
                  placeholder="+000"
                  disabled
                />
                <input
                  style={phoneInput(Boolean(errors.whatsapp))}
                  value={localPhone}
                  placeholder="e.g. 712345678"
                  inputMode="numeric"
                  onChange={(e) => {
                    setLocalPhone(e.target.value.replace(/\D/g, ""));
                    setErrors((prev) => ({ ...prev, whatsapp: "" }));
                  }}
                />
              </div>
            </Field>
            <Field
              label="Language"
              helper="Preferred communication language"
              error={errors.spokenLanguage}
            >
              <input
                list="language-options"
                style={inputStyle(Boolean(errors.spokenLanguage))}
                value={form.spokenLanguage}
                placeholder="Select or search language"
                onChange={(e) => update("spokenLanguage", e.target.value)}
              />
              <datalist id="language-options">
                {LANGUAGE_OPTIONS.map((language) => (
                  <option key={language} value={language} />
                ))}
              </datalist>
            </Field>
          </div>
        </section>

        {error ? <p style={errorText}>{error}</p> : null}

        <button type="submit" style={button} disabled={saving}>
          {saving ? "Saving..." : "Save and Continue"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, helper, error, children }) => (
  <label style={field}>
    <span style={labelStyle}>{label}</span>
    {children}
    <span style={error ? helperError : helperText}>{error || helper}</span>
  </label>
);

export default AdminProfileSetup;

const page = {
  minHeight: "calc(100vh - 112px)",
  background:
    "radial-gradient(circle at top left, rgba(14,85,128,0.08), transparent 32%), linear-gradient(180deg, #f7fbff 0%, #eef4f8 100%)",
  display: "grid",
  placeContent: "center",
  padding: "32px 20px"
};

const card = {
  width: "min(760px, 94vw)",
  background: "rgba(255,255,255,0.96)",
  border: "1px solid #dbe5ee",
  borderRadius: "24px",
  boxShadow: "0 18px 48px rgba(14,85,128,0.12)",
  padding: "22px",
  display: "grid",
  gap: "14px",
  backdropFilter: "blur(10px)"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  color: "#0E5580",
  fontSize: "clamp(24px, 2.6vw, 30px)"
};

const subtitle = {
  margin: "8px 0 0",
  color: "#526579",
  fontSize: "14px",
  lineHeight: 1.5,
  maxWidth: "54ch"
};

const locationButton = {
  border: "1px solid #bfd3e3",
  borderRadius: "14px",
  minHeight: "44px",
  padding: "0 14px",
  background: "#f8fbfe",
  color: "#0E5580",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer",
  transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease"
};

const hintBanner = {
  margin: 0,
  padding: "12px 14px",
  borderRadius: "16px",
  background: "#f4f9fd",
  color: "#486274",
  fontSize: "13px",
  border: "1px solid #d9e8f2"
};

const sectionCard = {
  background: "#fcfeff",
  border: "1px solid #d9e5ef",
  borderRadius: "20px",
  padding: "16px",
  display: "grid",
  gap: "12px"
};

const sectionHeader = {
  display: "grid",
  gap: "4px"
};

const sectionTitle = {
  margin: 0,
  color: "#143c57",
  fontSize: "18px"
};

const sectionText = {
  margin: 0,
  color: "#64748b",
  fontSize: "13px",
  lineHeight: 1.5
};

const gridTwo = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px"
};

const field = {
  display: "grid",
  gap: "8px"
};

const labelStyle = {
  fontSize: "13px",
  color: "#243b53",
  fontWeight: "700"
};

const inputBase = {
  border: "1px solid #c8d6e3",
  borderRadius: "14px",
  padding: "14px 16px",
  fontSize: "16px",
  color: "#0f172a",
  background: "#fff",
  outline: "none",
  transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
  minHeight: "50px",
  boxSizing: "border-box",
  width: "100%"
};

const inputStyle = (hasError) => ({
  ...inputBase,
  borderColor: hasError ? "#dc2626" : inputBase.borderColor,
  boxShadow: hasError
    ? "0 0 0 4px rgba(220, 38, 38, 0.08)"
    : "0 0 0 0 rgba(14, 85, 128, 0)"
});

const helperText = {
  fontSize: "12px",
  color: "#64748b",
  lineHeight: 1.5
};

const helperError = {
  ...helperText,
  color: "#b91c1c"
};

const phoneRow = {
  display: "grid",
  gridTemplateColumns: "132px 1fr",
  gap: "10px"
};

const codeInput = {
  ...inputBase,
  background: "#f8fbfe",
  color: "#486274",
  fontWeight: "700"
};

const phoneInput = (hasError) => ({
  ...inputStyle(hasError)
});

const button = {
  border: "none",
  borderRadius: "14px",
  minHeight: "52px",
  padding: "12px 16px",
  background: "linear-gradient(135deg, #0E5580 0%, #1b6f9e 100%)",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "800",
  cursor: "pointer",
  boxShadow: "0 14px 28px rgba(14,85,128,0.2)",
  minWidth: "220px"
};

const errorText = {
  margin: 0,
  color: "#b91c1c",
  background: "#fff5f5",
  border: "1px solid #fecaca",
  borderRadius: "14px",
  padding: "14px 16px"
};
